import React from 'react';
import { SkeletonMetricCard } from '../../shared/Skeleton';
import { TONE_STYLES, ICON_PATHS } from '../../shared/MetricCardShell';

/**
 * A headline stat card. Two looks:
 *  - default (no `tone`): the plain bordered card AI Visibility still uses;
 *  - `tone` + `icon`: the Competitive Intelligence card — tinted background, ring,
 *    watermark icon and a coloured value — built from the same TONE_STYLES and
 *    ICON_PATHS as `MetricCardShell`, so the two pages stay visually identical.
 * On Route Intelligence the tone is decorative (it tells the cards apart); no value
 * is coloured by a threshold.
 *
 * A toned card carries its colour as a 3px bar on its top edge by default. Pass
 * `accent="left"` to put a 4px bar on the left edge instead (the bar's colour is the
 * tone's). The class strings are written out in full so Tailwind's scan finds them.
 */
const LEFT_ACCENT = {
  emerald: 'border-l-emerald-500',
  amber: 'border-l-amber-500',
  red: 'border-l-red-500',
  gray: 'border-l-gray-300 dark:border-l-gray-700',
  brandBlue: 'border-l-[#32B1FF]',
  purple: 'border-l-purple-500',
};

export default function MetricCard({ label, value, suffix = '', hint, footer, loading, tint, tone, icon, accent = 'top' }) {
  // Shimmer while loading rather than an em-dash: '—' is the same glyph this
  // card uses for "loaded, but no value", so the two states were
  // indistinguishable. AIM's dashboard already loads this way
  // (SkeletonDashboard); RI was the outlier.
  if (loading) return <SkeletonMetricCard />;

  const hasValue = !(value == null || value === '');
  const body = hasValue ? (
    <>
      <span>{value}</span>
      {suffix ? <span>{suffix}</span> : null}
    </>
  ) : '—';

  if (!tone) {
    return (
      <div
        className={`rounded-xl border border-gray-200 dark:border-gray-800 p-4 ${tint || 'bg-white dark:bg-gray-900'}`}
      >
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</div>
        <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{body}</div>
        {hint && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</div>}
        {footer && <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">{footer}</div>}
      </div>
    );
  }

  const style = TONE_STYLES[tone] ?? TONE_STYLES.brandBlue;
  const edge = accent === 'left'
    ? `border-l-4 ${LEFT_ACCENT[tone] ?? LEFT_ACCENT.brandBlue}`
    : `border-t-[3px] ${style.borderTop}`;
  return (
    <div
      data-tone={tone}
      className={`relative ${style.bg ?? 'bg-white dark:bg-gray-900'} ${style.ring ?? ''} rounded-xl shadow-sm border dark:border-gray-800 ${edge} p-4 flex flex-col overflow-hidden`}
    >
      <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none" aria-hidden="true">
        <div className={`absolute -top-4 -right-4 ${style.watermark}`}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            {ICON_PATHS[icon] ?? ICON_PATHS.target}
          </svg>
        </div>
      </div>
      <div className="relative z-10 text-[13px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`relative z-10 mt-2 text-3xl font-extrabold leading-none ${hasValue ? style.valueClass : 'text-gray-400 dark:text-gray-500'}`}>{body}</div>
      {hint && <div className="relative z-10 mt-2 text-sm text-gray-500 dark:text-gray-400">{hint}</div>}
      {/* mt-auto pins the footer to the bottom of the card, so footers line up across
          a row of equal-height cards whether or not a card has a hint line. */}
      {footer && <div className="relative z-10 mt-auto pt-2 text-sm font-semibold text-gray-700 dark:text-gray-200">{footer}</div>}
    </div>
  );
}
